<template>
    <el-dialog
        title="发送卡密短信"
        :visible.sync="dialogVisible"
        width="1000px"
        custom-class="send-card-sms-dialog"
        :close-on-click-modal="false"
        @closed="onClosed"
    >
        <div class="sms-form-container">
            <!-- 第一列：确认客户信息 + 发放规则选择 -->
            <div class="form-column column-left">
                <!-- 1. 确认客户信息 -->
                <div class="form-section">
                    <div class="section-header">
                        <span class="step-number">1</span>
                        <span class="section-title">确认客户信息</span>
                    </div>
                    <div class="customer-info-card">
                        <div class="info-row">
                            <span class="info-label">客户姓名</span>
                            <span class="info-value">{{ customerInfo.name }}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">手机号</span>
                            <span class="info-value">{{ customerInfo.phone }}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">充值金额</span>
                            <span class="info-value">¥{{ formatMoney(customerInfo.rechargeMoney) }}</span>
                        </div>
                        <div class="info-row" v-if="customerInfo.bindingCard > 0">
                            <span class="info-label">已绑定</span>
                            <span class="info-value">{{ customerInfo.bindingCard }} 张卡密</span>
                        </div>
                    </div>
                </div>

                <!-- 2. 发放规则选择 -->
                <div class="form-section">
                    <div class="section-header">
                        <span class="step-number">2</span>
                        <span class="section-title">发放规则选择</span>
                    </div>
                    <div class="rule-options">
                        <label class="rule-option" :class="{ disabled: customerInfo.bindingCard > 0 }">
                            <input
                                type="radio"
                                name="sendRule"
                                v-model="sendRule"
                                value="byAmount"
                                :disabled="customerInfo.bindingCard > 0"
                            />
                            <span class="rule-radio"
                                :class="{ checked: sendRule === 'byAmount', disabled: customerInfo.bindingCard > 0 }"
                            ></span>
                            <span class="rule-text">
                                <span class="rule-title">自动匹配</span>
                                <span class="rule-desc">{{ customerInfo.bindingCard > 0 ? '已绑定卡密' : '自动匹配与充值金额一致的卡密' }}</span>
                            </span>
                        </label>
                        <label class="rule-option" :class="{ disabled: customerInfo.bindingCard > 0 }">
                            <input
                                type="radio"
                                name="sendRule"
                                v-model="sendRule"
                                value="manual"
                                :disabled="customerInfo.bindingCard > 0"
                            />
                            <span class="rule-radio"
                                :class="{ checked: sendRule === 'manual', disabled: customerInfo.bindingCard > 0 }"
                            ></span>
                            <span class="rule-text">
                                <span class="rule-title">手动选卡</span>
                                <span class="rule-desc">{{ customerInfo.bindingCard > 0 ? '已绑定卡密' : '从卡密列表勾选，总金额 ≤ 充值金额' }}</span>
                            </span>
                        </label>
                    </div>
                </div>
            </div>

            <!-- 第二列：选择卡密 -->
            <div class="form-column column-center">
                <div class="form-section">
                    <div class="section-header">
                        <span class="step-number">3</span>
                        <span class="section-title">{{ customerInfo.bindingCard > 0 ? '已绑定卡密' : '选择卡密' }}</span>
                    </div>

                    <!-- 已绑定卡密：直接展示明细 -->
                    <template v-if="customerInfo.bindingCard > 0">
                        <div class="selected-cards-summary">
                            <div class="selected-list">
                                <div v-for="card in availableCards" :key="card.id" class="selected-card-item">
                                    <span class="card-id">{{ card.account }}</span>
                                    <span class="card-pwd">密码: {{ card.password }}</span>
                                    <span class="card-value">{{ card.amountLabel }}</span>
                                </div>
                            </div>
                            <div class="summary-footer">
                                <div class="summary-item">
                                    <span class="summary-label">共计数量</span>
                                    <span class="summary-value">{{ availableCards.length }} 张</span>
                                </div>
                                <div class="summary-item total">
                                    <span class="summary-label">总金额</span>
                                    <span class="summary-value">{{ totalAmount }}</span>
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- 未绑定卡密：自动匹配 -->
                    <div v-else-if="sendRule === 'byAmount'" class="card-select-by-amount">
                        <div v-if="matchedCard" class="auto-match-result">
                            <span class="match-label">系统已为您匹配：</span>
                            <div class="selected-card-item">
                                <span class="card-id">{{ matchedCard.account }}</span>
                                <span class="card-pwd">密码: {{ matchedCard.password }}</span>
                                <span class="card-value">{{ matchedCard.amountLabel }}</span>
                            </div>
                        </div>
                        <div v-else class="empty-state">暂无可匹配的卡密</div>
                    </div>

                    <!-- 未绑定卡密：手动选卡 -->
                    <div v-else class="card-select-manual">
                        <div class="card-list-header">
                            可选卡密列表（充值金额 ¥{{ formatMoney(customerInfo.rechargeMoney) }}，已选 ¥{{ formatMoney(selectedTotalNumber) }}）
                        </div>
                        <div class="card-list">
                            <label
                                v-for="card in availableCards"
                                :key="card.id"
                                class="card-checkbox"
                            >
                                <input
                                    type="checkbox"
                                    :checked="selectedCards.includes(card.id)"
                                    :disabled="!selectedCards.includes(card.id) && (selectedTotalNumber + Number(card.amount)) > (customerInfo.rechargeMoney || 0)"
                                    @change="toggleCardSelection(card.id)"
                                />
                                <span
                                    class="card-checkbox-box"
                                    :class="{
                                        checked: selectedCards.includes(card.id),
                                        disabled: !selectedCards.includes(card.id) && (selectedTotalNumber + Number(card.amount)) > (customerInfo.rechargeMoney || 0)
                                    }"
                                >
                                    <i v-if="selectedCards.includes(card.id)" class="el-icon-check" />
                                </span>
                                <span class="card-info">
                                    <span class="card-account">{{ card.account }}</span>
                                    <span class="card-amount">{{ card.amountLabel }}</span>
                                </span>
                            </label>
                        </div>
                    </div>

                    <!-- 未绑定时：选中卡密明细 -->
                    <div v-if="customerInfo.bindingCard <= 0" class="selected-cards-summary">
                        <div class="summary-header">选中卡密明细</div>
                        <div class="selected-list" v-if="hasSelectedCards">
                            <div v-for="card in getSelectedCardDetails" :key="card.id" class="selected-card-item">
                                <span class="card-id">{{ card.account }}</span>
                                <span class="card-pwd">密码: {{ card.password }}</span>
                                <span class="card-value">{{ card.amount }}</span>
                            </div>
                        </div>
                        <div class="empty-state" v-else>尚未选择卡密</div>
                        <div class="summary-footer">
                            <div class="summary-item">
                                <span class="summary-label">共计数量</span>
                                <span class="summary-value">{{ selectedCount }} 张</span>
                            </div>
                            <div class="summary-item total">
                                <span class="summary-label">总金额</span>
                                <span class="summary-value">{{ totalAmount }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 第三列：短信模板与预览 -->
            <div class="form-column column-right">
                <div class="form-section">
                    <div class="section-header">
                        <span class="step-number">4</span>
                        <span class="section-title">短信模板与预览</span>
                    </div>
                    <div class="form-item">
                        <span class="form-label">选择短信模板</span>
                        <el-select v-model="selectedTemplate" placeholder="请选择短信模板" class="template-select">
                            <el-option v-for="template in smsTemplates" :key="template.id" :label="template.name" :value="template.id" />
                        </el-select>
                    </div>
                    <div class="sms-preview">
                        <div class="preview-header">短信内容预览</div>
                        <el-input type="textarea" :rows="2" placeholder="短信内容" v-model="customSmsContent" readonly />
                     
                    </div>
                </div>
            </div>
        </div>

        <span slot="footer" class="dialog-footer">
            <el-button @click="closeDialog">取消</el-button>
            <el-button class="btn-black" type="primary" :loading="submitting" :disabled="!hasSelectedCards" @click="handleSubmit">
                确认发放并发送
            </el-button>
        </span>
    </el-dialog>
</template>

<script>
import { getCardList } from '@/api/card'
import { getSmsTemplateList, sendSms } from '@/api/sms'
import { getOwnerDetail } from '@/api/customer'

export default {
    name: 'SendCardSmsDialog',
    props: {
        visible: { type: Boolean, default: false },
        customer: {
            type: Object,
            default: () => ({ name: '', phone: '', bindingCard: 0, rechargeMoney: 0, ownerId: null })
        }
    },
    data() {
        return {
            dialogVisible: false,
            sendRule: 'byAmount',
            selectedCards: [],
            selectedTemplate: '',
            customSmsContent: '',
            submitting: false,
            availableCards: [],
            smsTemplates: [],
            loadingCards: false,
            loadingTemplates: false,
            // 从接口获取的客户详情
            customerDetail: null,
            loadingDetail: false,
            // 原始卡密数据（用于提交时构建 cardInfoList）
            rawCardDataMap: {}
        }
    },
    computed: {
        // 合并 prop 与接口详情的客户信息
        customerInfo() {
            if (this.customerDetail) {
                return {
                    name: this.customerDetail.ownerName || this.customer.name,
                    phone: this.customerDetail.ownerPhone || this.customer.phone,
                    rechargeMoney: this.customerDetail.rechargeMoney != null ? this.customerDetail.rechargeMoney : this.customer.rechargeMoney,
                    bindingCard: this.customerDetail.bindingCard != null ? this.customerDetail.bindingCard : this.customer.bindingCard,
                    ownerId: this.customer.ownerId
                }
            }
            return this.customer
        },
        matchedCard() {
            const money = this.customerInfo.rechargeMoney
            if (!money) return null
            return this.availableCards.find((c) => Number(c.amount) === Number(money)) || null
        },
        selectedTotalNumber() {
            return this.selectedCards.reduce((sum, id) => {
                const card = this.availableCards.find((c) => c.id === id)
                return sum + (card ? Number(card.amount) || 0 : 0)
            }, 0)
        },
        selectedCount() {
            if (this.customerInfo.bindingCard > 0) {
                return this.availableCards.length
            }
            if (this.sendRule === 'byAmount') {
                return this.matchedCard ? 1 : 0
            }
            return this.selectedCards.length
        },
        totalAmount() {
            const total = this.customerInfo.bindingCard > 0
                ? this.availableCards.reduce((sum, c) => sum + (Number(c.amount) || 0), 0)
                : this.selectedTotalNumber
            return `¥${total.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        },
        hasSelectedCards() {
            if (this.customerInfo.bindingCard > 0) {
                return this.availableCards.length > 0
            }
            if (this.sendRule === 'byAmount') {
                return !!this.matchedCard
            }
            return this.selectedCount > 0
        },
        getSelectedCardDetails() {
            if (this.customerInfo.bindingCard > 0) {
                return this.availableCards.map((c) => ({ ...c, amount: c.amountLabel }))
            }
            if (this.sendRule === 'byAmount') {
                return this.matchedCard ? [{ ...this.matchedCard, amount: this.matchedCard.amountLabel }] : []
            }
            return this.availableCards
                .filter((c) => this.selectedCards.includes(c.id))
                .map((c) => ({ ...c, amount: c.amountLabel }))
        },
        currentTemplateContent() {
            const template = this.smsTemplates.find((t) => t.id === this.selectedTemplate)
            if (!template) return ''
            let content = template.content
            content = content.replace(/\$\{customerName\}/g, this.customerInfo.name)
            content = content.replace(/\$\{count\}/g, this.selectedCount)
            content = content.replace(/\$\{amount\}/g, this.totalAmount)
            const cardInfo = this.getSelectedCardDetails.map((c) => `卡号: ${c.account}, 密码: ${c.password}`).join('；')
            content = content.replace(/\$\{cardInfo\}/g, cardInfo)
            return content
        }
    },
    watch: {
        visible: {
            immediate: true,
            handler(val) {
                this.dialogVisible = val
                if (val) {
                    this.fetchCustomerDetail()
                    this.fetchTemplates()
                }
            }
        },
        sendRule() { this.selectedCards = [] },
        selectedTemplate() { this.customSmsContent = this.currentTemplateContent },
        selectedCards: { handler() { this.updateSmsContent() }, deep: true }
    },
    methods: {
        formatMoney(val) {
            if (val == null) return '0.00'
            return Number(val).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },
        async fetchCustomerDetail() {
            if (!this.customer.ownerId) {
                await this.fetchCards()
                return
            }
            this.loadingDetail = true
            try {
                const res = await getOwnerDetail(this.customer.ownerId)
                const data = res.data || res
                this.customerDetail = data

                // 根据 bindingCard 数量决定卡密来源
                if (this.customerInfo.bindingCard > 0) {
                    // 已绑定：使用详情接口返回的 cardList
                    const boundCardList = data.cardList || []
                    this.rawCardDataMap = {}
                    this.availableCards = boundCardList.map((item) => {
                        const id = item.cardId || item.id
                        this.rawCardDataMap[id] = item
                        return {
                            id,
                            account: item.cardNum || '',
                            password: item.cardPsw || item.cardPwd || '',
                            amount: item.cardMoney,
                            amountLabel: item.cardMoney != null
                                ? `¥${Number(item.cardMoney).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : ''
                        }
                    })
                } else {
                    // 未绑定：拉取可用卡密列表
                    await this.fetchCards()
                }
            } catch {
                // 详情接口异常时兜底拉取可用卡密
                await this.fetchCards()
            } finally {
                this.loadingDetail = false
            }
        },
        async fetchCards() {
            this.loadingCards = true
            try {
                const params = { pageSize: 999, cardStatus: 1 }
                const result = await getCardList(params)
                const rows = result.data || result.rows || []
                this.rawCardDataMap = {}
                this.availableCards = rows.map((item) => {
                    const id = item.id || item.cardId
                    this.rawCardDataMap[id] = item
                    return {
                        id,
                        account: item.cardNum || '',
                        password: item.cardPsw || item.cardPwd || '',
                        amount: item.cardMoney,
                        amountLabel: item.cardMoney != null
                            ? `¥${Number(item.cardMoney).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : ''
                    }
                })
            } catch { /* 加载失败 */ } finally { this.loadingCards = false }
        },
        async fetchTemplates() {
            this.loadingTemplates = true
            try {
                const result = await getSmsTemplateList({ reviewStatus: 0, pageSize: 999 })
                const rows = result.data || result.rows || []
                this.smsTemplates = rows.map((item) => ({
                    id: item.templateId,
                    name: item.templateName || '',
                    content: item.templateContent || ''
                }))
                if (this.smsTemplates.length && !this.selectedTemplate) {
                    this.selectedTemplate = this.smsTemplates[0].id
                }
            } catch { /* 加载失败 */ } finally { this.loadingTemplates = false }
        },
        closeDialog() {
            this.dialogVisible = false
            this.$emit('update:visible', false)
        },
        toggleCardSelection(cardId) {
            const index = this.selectedCards.indexOf(cardId)
            if (index > -1) {
                this.selectedCards.splice(index, 1)
                return
            }
            const card = this.availableCards.find((c) => c.id === cardId)
            const newTotal = this.selectedTotalNumber + (card ? Number(card.amount) : 0)
            if (newTotal > (this.customerInfo.rechargeMoney || 0)) {
                this.$message.warning('所选卡密总金额不能超过充值金额')
                return
            }
            this.selectedCards.push(cardId)
        },
        updateSmsContent() {
            const template = this.smsTemplates.find((t) => t.id === this.selectedTemplate)
            if (!template) return
            let content = template.content
            content = content.replace(/\$\{customerName\}/g, this.customerInfo.name)
            content = content.replace(/\$\{count\}/g, this.selectedCount)
            content = content.replace(/\$\{amount\}/g, this.totalAmount)
            const cardInfo = this.getSelectedCardDetails.map((c) => `卡号: ${c.account}, 密码: ${c.password}`).join('；')
            content = content.replace(/\$\{cardInfo\}/g, cardInfo)
            this.customSmsContent = content
        },
        onClosed() {
            this.submitting = false
            this.sendRule = 'byAmount'
            this.selectedCards = []
            this.selectedTemplate = ''
            this.customerDetail = null
            this.$emit('update:visible', false)
        },
        handleSubmit() {
            if (!this.hasSelectedCards) {
                this.$message.warning('请先选择卡密')
                return
            }
            if (this.customerInfo.bindingCard <= 0 && this.sendRule === 'manual' && this.selectedTotalNumber > (this.customerInfo.rechargeMoney || 0)) {
                this.$message.warning('总金额不能超过充值金额')
                return
            }
            if (!this.selectedTemplate) {
                this.$message.warning('请选择短信模板')
                return
            }

            // 构建 cardInfoList
            let cardIds = []
            if (this.customerInfo.bindingCard > 0) {
                cardIds = this.availableCards.map((c) => c.id)
            } else if (this.sendRule === 'byAmount') {
                if (this.matchedCard) cardIds = [this.matchedCard.id]
            } else {
                cardIds = this.selectedCards
            }
            const cardInfoList = cardIds.map((id) => this.rawCardDataMap[id]).filter(Boolean)

            this.submitting = true
            sendSms({
                cardInfoList,
                phoneNumber: this.customerInfo.phone,
                templateId: this.selectedTemplate
            }).then(() => {
                this.$message.success('发送成功')
                this.closeDialog()
                this.$emit('success', { customer: this.customerInfo, sendRule: this.sendRule, count: this.selectedCount, amount: this.totalAmount })
            }).catch(() => {
                // 错误已在 request.js 拦截器中处理
            }).finally(() => {
                this.submitting = false
            })
        }
    }
}
</script>

<style scoped>
.sms-form-container {
    display: flex;
    gap: 20px;
}

.form-column {
    flex: 1;
}

.form-section {
    background: #fff;
    border-radius: 8px;
    padding: 16px;
    border: 1px solid #e4e7ed;
}

.section-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e4e7ed;
}

.step-number {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #e4e7ed;
    color: #909399;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.section-title {
    font-size: 14px;
    font-weight: 600;
    color: #606266;
}

.customer-info-card {
    background: #f8f9fa;
    border-radius: 6px;
    padding: 12px;
}

.info-row {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
}

.info-row:not(:last-child) {
    border-bottom: 1px dashed #e4e7ed;
}

.info-label {
    color: #909399;
    font-size: 13px;
}

.info-value {
    color: #303133;
    font-weight: 500;
    font-size: 14px;
}

.info-value i {
    margin-left: 6px;
    color: #c0c4cc;
    cursor: pointer;
}

.rule-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.rule-option {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px;
    background: #f8f9fa;
    border: 2px solid transparent;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
}

.rule-option:hover {
    border-color: #d9ecff;
}

.rule-option input[type='radio'] {
    display: none;
}

.rule-radio {
    width: 18px;
    height: 18px;
    border: 2px solid #d9d9d9;
    border-radius: 50%;
    margin-top: 2px;
    position: relative;
    flex-shrink: 0;
}

.rule-radio.checked {
    border-color: #409eff;
}

.rule-radio.checked::after {
    content: '';
    position: absolute;
    top: 2.1px;
    left: 2.1px;
    width: 10px;
    height: 10px;
    background: #409eff;
    border-radius: 50%;
}

.rule-option.disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.rule-option.disabled:hover {
    border-color: transparent;
}

.rule-radio.disabled {
    background: #f0f0f0;
    cursor: not-allowed;
}

.auto-match-result {
    padding: 12px;
    background: #f0f9eb;
    border: 1px solid #e1f3d8;
    border-radius: 6px;
}

.match-label {
    font-size: 13px;
    color: #67c23a;
    font-weight: 500;
    display: block;
    margin-bottom: 8px;
}

.card-checkbox-box.disabled {
    background: #f0f0f0;
    border-color: #e0e0e0;
    cursor: not-allowed;
}

.card-checkbox.disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.rule-text {
    flex: 1;
}

.rule-title {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #303133;
}

.rule-desc {
    display: block;
    font-size: 12px;
    color: #909399;
    margin-top: 2px;
}

.form-item {
    margin-bottom: 16px;
}

.form-label {
    display: block;
    font-size: 13px;
    color: #606266;
    margin-bottom: 8px;
}

.amount-select,
.template-select {
    width: 100%;
}

.count-input {
    width: 100%;
}

.count-input >>> .el-input-number__decrease,
.count-input >>> .el-input-number__increase {
    height: 36px;
}

.card-select-manual {
    margin-bottom: 16px;
}

.card-list-header {
    font-size: 13px;
    color: #606266;
    margin-bottom: 12px;
}

.card-list {
    max-height: 180px;
    overflow-y: auto;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    padding: 4px;
}

.card-checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 4px;
    margin-bottom: 2px;
    cursor: pointer;
    transition: background 0.2s;
}

.card-checkbox:hover {
    background: #f5f7fa;
}

.card-checkbox input[type='checkbox'] {
    display: none;
}

.card-checkbox-box {
    width: 16px;
    height: 16px;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: #fff;
    flex-shrink: 0;
    background: #fff;
}

.card-checkbox-box.checked {
    background: #409eff;
    border-color: #409eff;
}

.card-info {
    flex: 1;
    display: flex;
    justify-content: space-between;
}

.card-account {
    font-size: 13px;
    color: #303133;
}

.card-amount {
    font-size: 13px;
    color: #f56c6c;
    font-weight: 500;
}

.selected-cards-summary {
    background: #f8f9fa;
    border-radius: 6px;
    padding: 12px;
}

.summary-header {
    font-size: 13px;
    font-weight: 500;
    color: #606266;
    margin-bottom: 10px;
}

.selected-list {
    max-height: 120px;
    overflow-y: auto;
    margin-bottom: 12px;
}

.selected-card-item {
    display: flex;
    justify-content: space-between;
    padding: 6px 0;
    font-size: 12px;
}

.selected-card-item:not(:last-child) {
    border-bottom: 1px dashed #e4e7ed;
}

.card-id {
    color: #303133;
}

.card-pwd {
    color: #606266;
}

.card-value {
    color: #409eff;
    font-weight: 500;
}

.empty-state {
    color: #909399;
    font-size: 13px;
    text-align: center;
    padding: 20px;
}

.summary-footer {
    display: flex;
    justify-content: space-between;
    padding-top: 10px;
    border-top: 1px solid #e4e7ed;
}

.summary-item {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

.summary-label {
    font-size: 12px;
    color: #909399;
}

.summary-value {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
}

.summary-item.total .summary-value {
    color: #f56c6c;
    font-size: 18px;
}

.sms-preview {
    background: #f8f9fa;
    border-radius: 6px;
    padding: 12px;
}

.preview-header {
    font-size: 13px;
    font-weight: 500;
    color: #606266;
    margin-bottom: 10px;
}

.preview-textarea {
    width: 100%;
    font-size: 13px;
    line-height: 1.6;
    color: #606266;
    resize: none;
}

.preview-textarea >>> textarea {
    background: #fff;
}

.preview-footer {
    font-size: 12px;
    color: #909399;
    text-align: right;
    margin-top: 10px;
}

.dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}

.dialog-footer .btn-black {
    width: 160px;
}
</style>